import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, AlertCircle, Info, X } from "lucide-react";
import { cn } from "@/lib/cn";

type ToastVariant = "success" | "error" | "info";
/** A button on the toast, such as "Buat asal" after a delete; such a toast stays longer. */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface Toast {
  id: number;
  message: string;
  variant: ToastVariant;
  action?: ToastAction;
}

interface ToastContextValue {
  toast: (message: string, variant?: ToastVariant, action?: ToastAction) => void;
  success: (message: string, action?: ToastAction) => void;
  error: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const icons = {
  success: CheckCircle2,
  error: AlertCircle,
  info: Info,
} as const;

const styles: Record<ToastVariant, string> = {
  success: "border-emerald-200 bg-emerald-50 text-emerald-800",
  error: "border-rose-200 bg-rose-50 text-rose-800",
  info: "border-slate-200 bg-white text-slate-800",
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const remove = useCallback((id: number) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const toast = useCallback(
    (message: string, variant: ToastVariant = "info", action?: ToastAction) => {
      const id = Date.now() + Math.random();
      setToasts((t) => [...t, { id, message, variant, action }]);
      setTimeout(() => remove(id), action ? 10_000 : 4500);
    },
    [remove],
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      toast,
      success: (m, action) => toast(m, "success", action),
      error: (m) => toast(m, "error"),
    }),
    [toast],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        // Portaled to <body> and z-[100] so notifications always sit above
        // modals (z-50) and the fullscreen editor overlay (z-[60]) — otherwise
        // an equal/lower z-index leaves them stuck behind a popup's backdrop.
        <div
          className="pointer-events-none fixed inset-x-4 bottom-4 z-[100] ml-auto flex w-auto max-w-sm flex-col gap-2 [body[data-sai]_&]:bottom-[6.5rem]"
          aria-live="polite"
          role="status"
        >
          {toasts.map((t) => {
            const Icon = icons[t.variant];
            return (
              <div
                key={t.id}
                className={cn(
                  "pointer-events-auto flex items-start gap-3 rounded-xl border p-3 shadow-pop animate-fade-in",
                  styles[t.variant],
                )}
              >
                <Icon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                <p className="flex-1 text-sm font-medium">{t.message}</p>
                {t.action && (
                  <button
                    type="button"
                    onClick={() => {
                      remove(t.id);
                      t.action!.onClick();
                    }}
                    className="shrink-0 rounded-lg bg-white/70 px-2.5 py-1 text-xs font-bold text-current hover:bg-white"
                    data-testid="toast-action"
                  >
                    {t.action.label}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => remove(t.id)}
                  className="rounded p-0.5 text-current/60 hover:text-current"
                  aria-label="Dismiss notification"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}
